import React, { useEffect, useState } from "react";
import axios from "axios";
import ReactPaginate from "react-paginate";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard, EmailGuard } from "../App";
import { FaSort } from "react-icons/fa";
import { Oval } from "react-loader-spinner";
import { StandardURL } from "../App";

const VerifyCodes = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const itemsPerPage = 10;

  useEffect(() => {
    fetchVerifyCodes();
  }, []);

  const fetchVerifyCodes = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(
        `${StandardURL}/iNyumba/viewverifycodes`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.response && Array.isArray(response.data.response)) {
        setCodes(response.data.response);
      } else {
        setCodes([]);
      }
    } catch (err) {
      setError("Failed to fetch verification codes.");
      toast.error(err.response?.data?.response || "Error fetching data.");
      console.error("Error fetching verification codes:", err);
    } finally {
      setLoading(false);
    }
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const sortedCodes = [...codes].sort((a, b) => {
    if (sortConfig.key !== null) {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
    }
    return 0;
  });

  const handlePageClick = (data) => {
    setCurrentPage(data.selected);
  };

  const offset = currentPage * itemsPerPage;
  const currentItems = sortedCodes.slice(offset, offset + itemsPerPage);
  const pageCount = Math.ceil(sortedCodes.length / itemsPerPage);

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="container mx-auto px-4 py-8 bg-gray-100 min-h-screen">
          <h1 className="text-3xl font-bold text-gray-800 text-center mb-6">Verification Codes</h1>

          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            {loading ? (
              <div className="flex justify-center items-center p-8">
                <Oval color="#4F46E5" height={50} width={50} />
              </div>
            ) : error ? (
              <div className="text-center p-8 text-red-500 font-semibold">
                {error}
              </div>
            ) : sortedCodes.length === 0 ? (
              <div className="text-center p-8 text-gray-500">
                No verification codes available.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {["user_id", "code", "intent", "otp_resend_attempts", "email_verify_token", "email_exp", "otp_exp", "created_at"].map((header) => (
                        <th
                          key={header}
                          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                          onClick={() => requestSort(header)}
                        >
                          <div className="flex items-center">
                            {header.replace(/_/g, ' ').toUpperCase()}
                            <FaSort className="ml-1 text-gray-400" />
                            {getSortIcon(header)}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentItems.map((code, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{code.user_id}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{code.code}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{code.intent}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{code.otp_resend_attempts}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{code.email_verify_token}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{code.email_exp}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{code.otp_exp}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(code.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {sortedCodes.length > 0 && (
              <div className="flex justify-center mt-6">
                <ReactPaginate
                  previousLabel={"Previous"}
                  nextLabel={"Next"}
                  breakLabel={"..."}
                  pageCount={pageCount}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={3}
                  onPageChange={handlePageClick}
                  containerClassName={"flex space-x-2"}
                  pageClassName={"px-4 py-2 text-sm font-medium border rounded-md transition-colors duration-150 ease-in-out"}
                  activeClassName={"bg-indigo-600 text-white border-indigo-600"}
                  previousClassName={"px-4 py-2 text-sm font-medium border rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"}
                  nextClassName={"px-4 py-2 text-sm font-medium border rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"}
                  disabledClassName={"opacity-50 cursor-not-allowed"}
                />
              </div>
            )}
          </div>
        </div>
      </AuthGuard>
    </EmailGuard>
  );
};

export default VerifyCodes;